import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Collapse,
  Alert,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SettingsIcon from '@mui/icons-material/Settings';
import SpeedIcon from '@mui/icons-material/Speed';
import DescriptionIcon from '@mui/icons-material/Description';
import TemplateIcon from '@mui/icons-material/AutoAwesomeMotion';
import CollectionsIcon from '@mui/icons-material/Collections';
import ArticleIcon from '@mui/icons-material/Article';
import HistoryIcon from '@mui/icons-material/History';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import GroupIcon from '@mui/icons-material/Group';
import SecurityIcon from '@mui/icons-material/Security';
import ExtensionIcon from '@mui/icons-material/Extension';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import TokenIcon from '@mui/icons-material/Token';
import { useGlobalSettingState, useComponentWebSocket } from './GlobalSettings/GlobalSettingUtils';
import { useWebSocket, useUser } from './WebSocketContext';
import { ModelSettings } from './GlobalSettings/ModelSettings';
import { PerformanceSettings } from './GlobalSettings/PerformanceSettingsNew';
import { GlobalSettingFiles } from './GlobalSettings/GlobalSettingFilesNew';
import { TemplateSettings } from './GlobalSettings/TemplateSettings';
import CollectionSettings from './GlobalSettings/CollectionSettings';
import GlobalSettingLogs from './GlobalSettings/GlobalSettingLogs';
import GlobalSettingActionsHistory from './GlobalSettings/GlobalSettingActionsHistory';
import AccountSettings from './GlobalSettings/AccountSettings';
import ModelManagement from './GlobalSettings/ModelManagement';
import ModelUsagesSettings from './GlobalSettings/ModelUsagesSettings';
import { PluginSettings } from './GlobalSettings/PluginSettings';
import AccountManagement from './AccountManagement';
import LoginActivitySettings from './GlobalSettings/LoginActivitySettings';
import { SystemSettings } from './GlobalSettings/SystemSettingsNew';

// Section panel helper with improved styling and mobile support
function SectionPanel(props) {
  const { children, value, index, ...other } = props;
  const visible = value === index;
  return (
    <div role="tabpanel" aria-hidden={!visible} {...other}>
      <Collapse in={visible} timeout={300} unmountOnExit>
        <Box sx={{
          p: { xs: 1.5, sm: 2.5, md: 3 },
          background: 'white',
          borderRadius: { xs: 1.5, sm: 2 },
          // Use transform for subtle reveal but do not force inner scrollbars
          transition: 'opacity 280ms ease, transform 300ms cubic-bezier(0.19, 1, 0.22, 1)',
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0px)' : 'translateY(6px)'
        }}>
          {children}
        </Box>
      </Collapse>
    </div>
  );
}

const GlobalSetting = () => {
  const { 
    credit,
    t
  } = useGlobalSettingState();
  const theme = useTheme();
  const isCompactNavigation = useMediaQuery(theme.breakpoints.down('md'));
  const { isAdmin, isEdit, permissions, username } = useUser();
  const { storageInfo, mfaSetupRequired } = useWebSocket();
  const isRestrictToUserFolder = Boolean(storageInfo?.restrictToUserFolder);
  const canManageGlobalSettings = isAdmin || isEdit;
  const canManageSystemSettings = isAdmin;

  const [tabValue, setTabValue] = useState(0);
  const [config, setConfig] = useState({
    modelTextHost: 'openai',
    modelTextHostId: '',
    modelVisionHost: 'openai',
    modelVisionHostId: '',
    modelAudioHost: 'openai',
    modelAudioHostId: '',
    modelWidgetHost: '',
    modelWidgetHostId: '',
    modelWidgetVisualHost: '',
    modelWidgetVisualHostId: '',
    modelProjectHost: '',
    modelProjectHostId: ''
  });
  const [allowedProviders, setAllowedProviders] = useState([]);
  const [savedModels, setSavedModels] = useState({
    text: [],
    vision: [],
    audio: [],
    widget_visual: []
  });

  // Define consistent colors and styling
  const primaryColor = '#5757b0';
  const lightPurple = '#f0f0ff';

  // Callback to update parent config state from child components
  const updateParentConfig = (newConfig) => {
    setConfig(prev => ({
      ...prev,
      ...newConfig
    }));
  };

  const normalizeAllowedProviders = useCallback((providers) => {
    if (!providers || !Array.isArray(providers)) return [];
    return providers.map((p) => (p ? String(p).toLowerCase() : '')).filter(Boolean);
  }, []);

  const filterSavedModels = useCallback((models, allowed = allowedProviders) => {
    const allowedSet = new Set((allowed || []).map((p) => p.toLowerCase()).filter(Boolean));
    const baseModels = models || {};
    const filterList = (list = []) => {
      if (!allowedSet.size) return list;
      return list.filter((model) => allowedSet.has((model.provider || '').toLowerCase()));
    };
    return {
      text: filterList(baseModels.text || []),
      vision: filterList(baseModels.vision || []),
      audio: filterList(baseModels.audio || []),
      widget_visual: filterList(baseModels.widget_visual || [])
    };
  }, [allowedProviders]);

  // References to child components to access their state
  const modelSettingsRef = useRef(null);
  const performanceSettingsRef = useRef(null);
  const formatSettingsRef = useRef(null);
  const templatesSettingsRef = useRef(null);
  const collectionsSettingsRef = useRef(null);
  const logsSettingsRef = useRef(null);
  const actionsHistorySettingsRef = useRef(null);
  const accountSettingsRef = useRef(null);

  // Define handlers for WebSocket messages
  const commandHandlers = {
    'update_configuration': (message) => {
      if (message.data === 'success') {
        // Each component will handle its own success message
        // Request updated configuration to refresh the display
        sendCommand('configuration_check');
      }
    },
    'configuration_data': (message) => {
      const allowed = normalizeAllowedProviders(message.data.allowedModelProviders || []);
      setAllowedProviders(allowed);
      setConfig({
        modelTextHost: message.data.modelTextHost || 'openai',
        modelTextHostId: message.data.modelTextHostId ? String(message.data.modelTextHostId) : '',
        modelVisionHost: message.data.modelVisionHost || 'openai',
        modelVisionHostId: message.data.modelVisionHostId ? String(message.data.modelVisionHostId) : '',
        modelAudioHost: message.data.model_audio_host || message.data.modelAudioHost || 'openai',
        modelAudioHostId: message.data.modelAudioHostId ? String(message.data.modelAudioHostId) : '',
        modelWidgetHost: message.data.modelWidgetHost || '',
        modelWidgetHostId: message.data.modelWidgetHostId ? String(message.data.modelWidgetHostId) : '',
        modelWidgetVisualHost: message.data.modelWidgetVisualHost || '',
        modelWidgetVisualHostId: message.data.modelWidgetVisualHostId ? String(message.data.modelWidgetVisualHostId) : '',
        modelProjectHost: message.data.modelProjectHost || '',
        modelProjectHostId: message.data.modelProjectHostId ? String(message.data.modelProjectHostId) : ''
      });
    },
    'configuration_check': () => {
      // Configuration check handling if needed
    },
    'saved_models': (message) => {
      const allowed = normalizeAllowedProviders(message.data?.allowedModelProviders || allowedProviders);
      if (allowed.length) {
        setAllowedProviders(allowed);
      }
      setSavedModels(filterSavedModels(message.data || { text: [], vision: [], audio: [], widget_visual: [] }, allowed.length ? allowed : allowedProviders));
    }
  };
  
  // Use our custom WebSocket hook
  const { sendCommand } = useComponentWebSocket(commandHandlers);

  useEffect(() => {
    setSavedModels((prev) => filterSavedModels(prev));
  }, [filterSavedModels, allowedProviders]);

  useEffect(() => {
    // Request configuration data when component mounts
    sendCommand('configuration_check');
    sendCommand('get_saved_models');
  }, [sendCommand]);

  const tabs = useMemo(() => {
    const baseTabs = [];

    if (canManageGlobalSettings) {
      baseTabs.push(
        {
          key: 'model-management',
          icon: <ManageAccountsIcon />,
          label: t('global_setting.model_management_section', 'Model Management'),
          panel: (
            <ModelManagement
              savedModels={savedModels}
              allowedModelProviders={allowedProviders}
              onModelsUpdate={() => sendCommand('get_saved_models')}
            />
          )
        },
        {
          key: 'model-settings',
          icon: <SettingsIcon />,
          label: t('global_setting.model_settings_section', 'Models Assignments'),
          panel: <ModelSettings ref={modelSettingsRef} onConfigChange={updateParentConfig} savedModels={savedModels} />
        },
        {
          key: 'model-usages',
          icon: <TokenIcon />,
          label: t('global_setting.models_usages_section', 'Models usages'),
          panel: <ModelUsagesSettings />
        },
        {
          key: 'performance',
          icon: <SpeedIcon />,
          label: t('global_setting.performance_section'),
          panel: <PerformanceSettings ref={performanceSettingsRef} />
        },
        {
          key: 'format',
          icon: <DescriptionIcon />,
          label: t('global_setting.format_section'),
          panel: <GlobalSettingFiles ref={formatSettingsRef} />
        },
        {
          key: 'templates',
          icon: <TemplateIcon />,
          label: t('global_setting.templates_section', 'Templates'),
          panel: <TemplateSettings ref={templatesSettingsRef} />
        },
        {
          key: 'plugins',
          icon: <ExtensionIcon />,
          label: t('global_setting.plugins_section', 'Plugins'),
          panel: <PluginSettings />
        },
        {
          key: 'collections',
          icon: <CollectionsIcon />,
          label: t('global_setting.collections_section', 'Collections'),
          panel: <CollectionSettings ref={collectionsSettingsRef} />
        }
      );
    }

    if (canManageGlobalSettings && !isRestrictToUserFolder) {
      baseTabs.push({
        key: 'logs',
        icon: <ArticleIcon />,
        label: t('global_setting.logs_section', 'Logs'),
        panel: <GlobalSettingLogs ref={logsSettingsRef} />
      });
    }

    if (canManageSystemSettings) {
      baseTabs.push({
        key: 'system',
        icon: <SettingsIcon />,
        label: t('global_setting.system_section', 'System & Runtime'),
        panel: <SystemSettings />
      });
    }

    if (canManageGlobalSettings) {
      baseTabs.push({
        key: 'actions-history',
        icon: <HistoryIcon />,
        label: t('global_setting.actions_history_section', 'Actions History'),
        panel: <GlobalSettingActionsHistory ref={actionsHistorySettingsRef} />
      });
    }

    if (isAdmin) {
      baseTabs.push({
        key: 'users-management',
        icon: <GroupIcon />,
        label: t('account_settings.tab_users_management', 'Users Management'),
        panel: (
          <AccountManagement
            userPermission={permissions}
            currentUsername={username}
          />
        )
      });
      baseTabs.push({
        key: 'login-activities',
        icon: <SecurityIcon />,
        label: t('account_settings.login_activity_title', 'Login Activity'),
        panel: <LoginActivitySettings />
      });
    }

    baseTabs.push({
      key: 'account',
      icon: <AccountCircleIcon />,
      label: t('global_setting.account_section', 'Account'),
      panel: <AccountSettings ref={accountSettingsRef} />
    });

    return baseTabs;
  }, [
    canManageGlobalSettings,
    canManageSystemSettings,
    isAdmin,
    isRestrictToUserFolder,
    t,
    savedModels,
    allowedProviders,
    sendCommand,
    updateParentConfig,
    permissions,
    username
  ]);

  const accountTabIndex = useMemo(() => {
    return tabs.findIndex((tab) => tab.key === 'account');
  }, [tabs]);

  const isTabDisabled = useCallback((tab) => {
    return mfaSetupRequired && tab?.key !== 'account';
  }, [mfaSetupRequired]);

  const handleTabChange = useCallback((newValue) => {
    const nextIndex = Number(newValue);
    if (Number.isNaN(nextIndex) || nextIndex < 0 || nextIndex >= tabs.length) {
      return;
    }
    if (isTabDisabled(tabs[nextIndex])) {
      return;
    }
    setTabValue(nextIndex);
  }, [isTabDisabled, tabs]);

  useEffect(() => {
    if (tabValue >= tabs.length) {
      setTabValue(0);
    }
  }, [tabValue, tabs.length]);

  useEffect(() => {
    if (mfaSetupRequired && accountTabIndex >= 0 && tabValue !== accountTabIndex) {
      setTabValue(accountTabIndex);
    }
  }, [mfaSetupRequired, accountTabIndex, tabValue]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: {
          xs: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          sm: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
        },
        py: { xs: 2, sm: 3, md: 4 },
        px: 0
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        <Paper
          elevation={12}
          sx={{
            background: 'white',
            border: `2px solid ${primaryColor}`,
            borderRadius: { xs: 2, sm: 3, md: 4 },
            boxShadow: {
              xs: `0px 8px 24px rgba(87, 87, 176, 0.2)`,
              sm: `0px 12px 36px rgba(87, 87, 176, 0.25)`,
              md: `0px 16px 48px rgba(87, 87, 176, 0.3), 0px 8px 24px rgba(87, 87, 176, 0.2)`
            },
            position: 'relative',
            overflow: 'hidden',
            maxWidth: { xs: '100%', sm: '95%', md: '90%', lg: '85%' },
            mx: 'auto',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: { xs: '3px', sm: '4px' },
              background: `linear-gradient(90deg, ${primaryColor} 0%, #7575c7 50%, ${primaryColor} 100%)`,
            }
          }}
        >
          <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            {/* Header Section with improved responsiveness */}
            <Box sx={{ 
              textAlign: 'center', 
              mb: { xs: 3, sm: 4 }, 
              mt: { xs: 1, sm: 2 }
            }}>
              <Typography 
                variant="h3" 
                gutterBottom 
                sx={{ 
                  fontWeight: 700,
                  color: primaryColor,
                  mb: { xs: 1.5, sm: 2 },
                  fontSize: { 
                    xs: '1.75rem', 
                    sm: '2.25rem', 
                    md: '2.75rem',
                    lg: '3rem'
                  },
                  letterSpacing: '0.02em',
                  lineHeight: { xs: 1.2, sm: 1.3 }
                }}
              >
                {t('global_setting.title')}
              </Typography>
              
              {(config.modelTextHost === 'dataunfold' || config.modelVisionHost === 'dataunfold' || 
                config.modelAudioHost === 'dataunfold' || config.modelTextHost === 'dataunfold_mock' || 
                config.modelVisionHost === 'dataunfold_mock' || config.modelAudioHost === 'dataunfold_mock') && (
                <Box
                  sx={{
                    mb: 2,
                    borderRadius: 1,
                    px: 1.5,
                    py: 0.75,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    border: '1px solid rgba(0,0,0,0.08)',
                    background: credit > 0 ? 'rgba(255,255,255,0.7)' : 'rgba(237, 108, 2, 0.08)',
                    backdropFilter: 'blur(4px)',
                    opacity: 0.9,
                    boxShadow: 'none'
                  }}
                >
                  <MonetizationOnIcon sx={{ fontSize: 18, color: credit > 0 ? '#FFD700' : '#ED6C02', opacity: 0.85 }} />
                  <Typography variant="body2" sx={{ fontWeight: 500, color: credit > 0 ? 'rgba(0,0,0,0.8)' : '#ED6C02', opacity: 0.85 }}>
                    {credit > 0
                      ? t('global_setting.credit_left', { credit })
                      : t('global_setting.no_credit_left')}
                  </Typography>
                </Box>
              )}
            </Box>

            {mfaSetupRequired && (
              <Alert severity="warning" sx={{ mb: { xs: 2, sm: 3 }, borderRadius: 2 }}>
                {t('account_management.mfa_setup_required', 'MFA setup is required. Please complete it in Account Settings to continue.')}
              </Alert>
            )}

            {/* Scalable section navigation (desktop list + mobile select) */}
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 2, sm: 3 },
              alignItems: 'stretch'
            }}>
              <Box sx={{ width: { xs: '100%', md: 290 }, flexShrink: 0 }}>
                {isCompactNavigation ? (
                  <FormControl fullWidth size="small">
                    <InputLabel id="global-settings-section-select-label">
                      {t('global_setting.select_section', 'Section')}
                    </InputLabel>
                    <Select
                      labelId="global-settings-section-select-label"
                      value={tabValue}
                      label={t('global_setting.select_section', 'Section')}
                      onChange={(event) => handleTabChange(event.target.value)}
                      sx={{
                        borderRadius: 1.5,
                        backgroundColor: lightPurple,
                        '& .MuiSelect-select': {
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }
                      }}
                    >
                      {tabs.map((tab, index) => (
                        <MenuItem
                          key={tab.key}
                          value={index}
                          disabled={isTabDisabled(tab)}
                        >
                          {tab.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      border: '1px solid rgba(87, 87, 176, 0.2)',
                      background: lightPurple,
                      boxShadow: '0px 2px 8px rgba(0,0,0,0.05)',
                      maxHeight: '75vh',
                      overflowY: 'auto'
                    }}
                  >
                    <List disablePadding>
                      {tabs.map((tab, index) => (
                        <ListItemButton
                          key={tab.key}
                          selected={tabValue === index}
                          disabled={isTabDisabled(tab)}
                          onClick={() => handleTabChange(index)}
                          sx={{
                            borderRadius: 1.5,
                            mb: 0.5,
                            px: 1.5,
                            py: 1.1,
                            '& .MuiListItemIcon-root': {
                              minWidth: 36,
                              color: tabValue === index ? primaryColor : '#666'
                            },
                            '& .MuiListItemText-primary': {
                              color: tabValue === index ? primaryColor : '#333',
                              fontWeight: tabValue === index ? 700 : 500,
                              fontSize: '0.92rem'
                            },
                            '&.Mui-selected': {
                              backgroundColor: 'rgba(87, 87, 176, 0.12)',
                              border: '1px solid rgba(87, 87, 176, 0.2)'
                            },
                            '&.Mui-selected:hover': {
                              backgroundColor: 'rgba(87, 87, 176, 0.16)'
                            },
                            '&:hover': {
                              backgroundColor: 'rgba(87, 87, 176, 0.07)'
                            }
                          }}
                        >
                          <ListItemIcon>{tab.icon}</ListItemIcon>
                          <ListItemText primary={tab.label} />
                        </ListItemButton>
                      ))}
                    </List>
                  </Paper>
                )}
              </Box>

              {/* Section Panels with improved mobile styling */}
              <Box sx={{
                flex: 1,
                minWidth: 0,
                borderRadius: { xs: 1.5, sm: 2 },
                minHeight: { xs: 300, sm: 350, md: 400 },
                position: 'relative',
                boxShadow: {
                  xs: '0px 2px 8px rgba(0,0,0,0.08)',
                  sm: '0px 2px 8px rgba(0,0,0,0.1)'
                }
              }}>
                {tabs.map((tab, index) => (
                  <SectionPanel key={tab.key} value={tabValue} index={index}>
                    {tab.panel}
                  </SectionPanel>
                ))}
              </Box>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default GlobalSetting;
